import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import PermitTracker from './components/PermitTracker';
import PermitTypeSelector from './components/PermitTypeSelector';
import BuildingPermitForm from './components/BuildingPermitForm';
import ROWPermitForm from './components/ROWPermitForm';
import IndyResidentialPermitSelector from './components/IndyResidentialPermitSelector';
import IndyResidentialDevelopmentForm from './components/IndyResidentialDevelopmentForm';
import IndyResidentialHVACForm from './components/IndyResidentialHVACForm';
import IndyResidentialPlumbingForm from './components/IndyResidentialPlumbingForm';
import IndyResidentialWreckingForm from './components/IndyResidentialWreckingForm';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import StartChoices from './components/StartChoices';
import AppealFlow from './components/AppealFlow';
import ReportType from './components/ReportType';
import CodeReportPage from './components/CodeReportPage';
import PublicReportPage from './components/PublicReportPage';
import CountySelector from './components/CountySelector';
import Login from './components/Login';
import StaffLogin from './components/StaffLogin';
import AdminRegister from './components/AdminRegister';
import LandingPage from './components/LandingPage';
import SignInChoice from './components/SignInChoice';
import './App.css';
import logoFavicon from './img/citylink_logo.webp';
import { API_BASE } from './config/apiConfig';

// Auth-only wrapper: redirect to login if not authenticated (used for /select)
const RequireAuth = ({ children }) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      let isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      if (!isAuthenticated) {
        try {
          const response = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
          if (response.data?.success && response.data.user) {
            const user = response.data.user;
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('userEmail', user.email);
            localStorage.setItem('userId', user.id);
            localStorage.setItem('currentUser', JSON.stringify(user));
            isAuthenticated = true;
          }
        } catch {}
      }
      if (!isAuthenticated) {
        navigate('/');
      }
      setIsChecking(false);
    };
    checkAuth();
  }, [navigate]);

  if (isChecking) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>Loading...</div>;
  }
  return children;
};

// Redirects admins from user routes to /admin
const RoleRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser') || 'null');
    if (user?.role === 'admin') {
      const isUserPath = location.pathname === '/dashboard' || location.pathname === '/submit' || location.pathname.startsWith('/status/');
      if (isUserPath) {
        navigate('/admin', { replace: true });
      }
    }
  }, [location.pathname, navigate]);
  return null;
};

// Admin-only route: requires auth, county selection, and role === 'admin'
const RequireAdmin = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      let user = null;
      if (localStorage.getItem('isAuthenticated') === 'true') {
        const stored = localStorage.getItem('currentUser');
        if (stored) {
          try {
            user = JSON.parse(stored);
          } catch {}
        }
      }
      if (!user) {
        try {
          const response = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
          if (response.data?.success && response.data.user) {
            user = response.data.user;
            localStorage.setItem('currentUser', JSON.stringify(user));
          }
        } catch {}
      }
      if (!user || user.role !== 'admin') {
        navigate('/dashboard');
        setIsChecking(false);
        return;
      }
      const selectedCounty = localStorage.getItem('selectedCounty');
      if (!selectedCounty) {
        navigate('/select');
        setIsChecking(false);
        return;
      }
      setIsChecking(false);
    };
    checkAdmin();
  }, [location.pathname, navigate]);

  if (isChecking) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>Loading...</div>;
  }
  return children;
};

// Protected Route: auth + county. Role-based redirects happen in layout.
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '/login' || location.pathname === '/select' || location.pathname.startsWith('/admin/register')) {
      setIsChecking(false);
      return;
    }

    const checkAuth = async () => {
      let isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      let user = null;

      if (!isAuthenticated) {
        try {
          const response = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
          if (response.data?.success && response.data.user) {
            user = response.data.user;
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('userEmail', user.email);
            localStorage.setItem('userId', user.id);
            localStorage.setItem('currentUser', JSON.stringify(user));
            isAuthenticated = true;
          }
        } catch {
          // ignore
        }
      } else {
        try {
          user = JSON.parse(localStorage.getItem('currentUser') || 'null');
        } catch {}
      }

      if (!isAuthenticated) {
        navigate('/');
        setIsChecking(false);
        return;
      }

      const selectedCounty = localStorage.getItem('selectedCounty');
      if (!selectedCounty) {
        navigate('/select');
        setIsChecking(false);
        return;
      }

      setIsChecking(false);
    };

    checkAuth();
  }, [location.pathname, navigate]);

  if (isChecking) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>Loading...</div>;
  }

  return children;
};

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedCounty, setSelectedCounty] = useState(null);

  useEffect(() => {
    // Set favicon to CityLink logo
    try {
      const existing = document.querySelector('link[rel="icon"]');
      const link = existing || document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      link.href = logoFavicon;
      if (!existing) {
        document.head.appendChild(link);
      }
    } catch (e) {
      // Ignore favicon errors
    }

    // Load selected county from localStorage
    const stored = localStorage.getItem('selectedCounty');
    if (stored) {
      try {
        setSelectedCounty(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse stored county:', e);
      }
    }
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/staff-login" element={<StaffLogin />} />
        <Route path="/sign-in" element={<SignInChoice />} />
        <Route path="/admin/register" element={<AdminRegister />} />
        <Route 
          path="/select" 
          element={
            <RequireAuth>
              <CountySelector />
            </RequireAuth>
          } 
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <div className={`App app-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <RoleRedirect />
        {/* Global hamburger that is always visible */} 
        <button
          className={`global-hamburger ${sidebarOpen ? 'is-active' : ''}`}
          aria-label="Toggle navigation"
          onClick={toggleSidebar}
        >
          <span />
          <span />
          <span />
        </button>

        {/* Backdrop for when sidebar is open */}
        <div
          className={`sidebar-backdrop ${sidebarOpen ? 'visible' : ''}`}
          onClick={closeSidebar}
        />

        <aside className={`sidebar ${sidebarOpen ? 'is-open' : ''}`}>
          <div className="sidebar-header">
            <Link to="/" className="sidebar-logo" onClick={closeSidebar}>
              <span className="sidebar-logo-mark">
                <img src={logoFavicon} alt="CityLink logo" className="sidebar-logo-img" />
              </span>
              <span className="sidebar-logo-text">
                <span className="sidebar-logo-city">CITY</span>
                <span className="sidebar-logo-link">LINK</span>
              </span>
            </Link>
            {selectedCounty && (
              <div className="sidebar-location">
                <span className="sidebar-location-icon" aria-hidden="true"></span>
                <div className="sidebar-location-text">
                  <div className="sidebar-location-city">{selectedCounty.city}</div>
                  <div className="sidebar-location-county">{selectedCounty.county}, {selectedCounty.stateAbbr}</div>
                </div>
              </div>
            )}
          </div>

          <nav className="sidebar-nav">
            <div className="sidebar-section-label">Workspace</div>
            {(() => {
              const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
              const isAdmin = currentUser?.role === 'admin';
              if (isAdmin) {
                return (
                  <NavLink
                    to="/admin"
                    className={({ isActive }) =>
                      'sidebar-link' + (isActive ? ' active' : '')
                    }
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-link-icon" aria-hidden="true"></span>
                    <span className="sidebar-link-text">Admin</span>
                  </NavLink>
                );
              }
              return (
                <>
                  <NavLink
                    to="/start"
                    className={({ isActive }) =>
                      'sidebar-link' + (isActive ? ' active' : '')
                    }
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-link-icon" aria-hidden="true"></span>
                    <span className="sidebar-link-text">Back to Start</span>
                  </NavLink>
                  <NavLink
                    end
                    to="/dashboard"
                    className={({ isActive }) =>
                      'sidebar-link' + (isActive ? ' active' : '')
                    }
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-link-icon" aria-hidden="true"></span>
                    <span className="sidebar-link-text">Dashboard</span>
                  </NavLink>
                  <NavLink
                    to="/submit"
                    className={({ isActive }) =>
                      'sidebar-link' + (isActive ? ' active' : '')
                    }
                    onClick={closeSidebar}
                  >
                    <span className="sidebar-link-icon" aria-hidden="true"></span>
                    <span className="sidebar-link-text">New Application</span>
                  </NavLink>
                </>
              );
            })()}
          </nav>

          <div className="sidebar-footer">
            <div className="sidebar-user">
              <div className="sidebar-user-avatar">
                {(() => {
                  const email = localStorage.getItem('userEmail') || 'User';
                  return email.substring(0, 2).toUpperCase();
                })()}
              </div>
              <div className="sidebar-user-meta">
                <div className="sidebar-user-name">{(() => {
                const u = JSON.parse(localStorage.getItem('currentUser') || 'null');
                return u?.role === 'admin' ? 'Admin' : 'User';
              })()}</div>
                <div className="sidebar-user-email">{localStorage.getItem('userEmail') || 'user@example.com'}</div>
              </div>
            </div>
            <button
              className="sidebar-logout-button"
              onClick={async () => {
                try {
                  // Always call logout API (auth is cookie-based; cookie is sent via withCredentials)
                  await axios.post(`${API_BASE}/auth/logout`, {}, {
                    withCredentials: true
                  });
                } catch (error) {
                  console.error('Logout error:', error);
                } finally {
                  // Clear all auth-related local storage
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('isAuthenticated');
                  localStorage.removeItem('userEmail');
                  localStorage.removeItem('userId');
                  localStorage.removeItem('loginTimestamp');
                  localStorage.removeItem('currentUser');
                  localStorage.removeItem('userSavedLocations');
                  sessionStorage.setItem('loggedOut', '1');
                  window.location.href = '/';
                }
              }}
            >
              Sign Out
            </button>
          </div>
        </aside>

        <main className="app-main">
          <div className="app-main-inner">
                <Routes>
                  <Route path="/start" element={<StartChoices />} />
                  <Route path="/appeal" element={<AppealFlow />} />
                  <Route path="/report-type" element={<ReportType />} />
                  <Route path="/code-report" element={<CodeReportPage />} />
                  <Route path="/public-report" element={<PublicReportPage />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/submit" element={<PermitTypeSelector />} />
                  {/* West Lafayette permits */}
                  <Route path="/submit/building" element={<BuildingPermitForm />} />
                  <Route path="/submit/row" element={<ROWPermitForm />} />
                  {/* Indianapolis – Class 2 Residential permits */}
                  <Route path="/submit/indy/residential" element={<IndyResidentialPermitSelector />} />
                  <Route path="/submit/indy/residential-development" element={<IndyResidentialDevelopmentForm />} />
                  <Route path="/submit/indy/residential-hvac" element={<IndyResidentialHVACForm />} />
                  <Route path="/submit/indy/residential-plumbing" element={<IndyResidentialPlumbingForm />} />
                  <Route path="/submit/indy/residential-wrecking" element={<IndyResidentialWreckingForm />} />
                  <Route path="/status/:submissionId" element={<PermitTracker />} />
                  <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
                </Routes>
              </div>
            </main>
          </div>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
